import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";

import { ErrorComponent } from "@/components/common/errors";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { API } from "@/services/API";
import { useMarketMap } from "./hooks/useMarketMap";
import { useMarketLayers } from "./hooks/useMarketLayers";
import { useMapPins } from "./hooks/useMapPins";

/**
 * Base component for rendering a market map with colored regions
 *
 * @example
 * ```jsx
 * import { MarketMapBase } from '@/components/maps/MarketMapBase';
 *
 * const MyComponent = () => {
 *   const mapRef = useRef(null);
 *
 *   return (
 *     <MarketMapBase
 *       outbounds={marketData}
 *       optimizePer={{ id: "rpm" }}
 *       onMarketClick={(market) => console.log(market)}
 *       ref={mapRef}
 *     >
 *       <ZoomControls onZoomIn={() => mapRef.current?.zoomIn()} />
 *     </MarketMapBase>
 *   );
 * };
 * ```
 */
export const MarketMapBase = forwardRef(
    (
        {
            // Data props
            outbounds,
            optimizePer = { id: "rpm" },

            // Interaction props
            onMarketClick,
            onError,
            onInitialised,

            // Display props
            selectedMarkets = [],
            showSkeleton: externalShowSkeleton,
            HoverPopupComponent,
            className = "",
            containerClassName = "",
            isLocked = false,

            // Children for overlay controls
            children,
        },
        ref,
    ) => {
        const [nationalAverageRPM, setNationalAverageRPM] = useState(null);
        const [showSkeleton, setShowSkeleton] = useState(true);
        const [hasError, setHasError] = useState(false);
        const isDarkTheme = useIsDarkTheme();

        const handleError = (e) => {
            setHasError(true);
            onError?.(e);
        };

        // Initialize map
        const {
            mapRef,
            mapContainer,
            mapIsLoaded,
            mapInitialised,
            markInitialised,
            error: mapError,
            padding,
        } = useMarketMap({
            onError: handleError,
        });

        // Initialize layers
        const { markets, marketCenters, states, updateMarketColors, addSelectedMarketLayer, removeSelectedMarketLayer, moveOverlayLayersToTop } = useMarketLayers({
            mapRef,
            mapIsLoaded,
            mapInitialised,
            markInitialised,
            outbounds,
            optimizePer,
            nationalAverageRPM,
            padding,
            HoverPopupComponent,
            isLocked,
        });

        // Pin management
        const pinManager = useMapPins();

        // Expose map methods via ref
        useImperativeHandle(
            ref,
            () => ({
                getMap: () => mapRef.current,
                zoomIn: () => mapRef.current?.zoomIn(),
                zoomOut: () => mapRef.current?.zoomOut(),
                fitBounds: (bounds, options) => mapRef.current?.fitBounds(bounds, options),
                addPin: pinManager.addPin,
                removeAllPins: pinManager.removeAllPins,
                addNamedPin: pinManager.addNamedPin,
                removeNamedPins: pinManager.removeNamedPins,
                addSelectedMarketLayer,
                removeSelectedMarketLayer,
                moveOverlayLayersToTop: () => moveOverlayLayersToTop(mapRef.current),
                isInitialised: () => mapInitialised,
                getMarkets: () => markets,
                getMarketCenters: () => marketCenters,
                getStates: () => states,
            }),
            [mapRef, mapInitialised, pinManager, addSelectedMarketLayer, removeSelectedMarketLayer, moveOverlayLayersToTop, markets, marketCenters, states],
        );

        // Load skeleton on map load
        useEffect(() => {
            if (!mapRef.current || !mapIsLoaded) return;

            const map = mapRef.current;

            const loadSkeleton = async () => {
                try {
                    const response = await fetch("/usa_continental.json");
                    const usaData = await response.json();

                    map.addSource("skeleton-usa", {
                        type: "geojson",
                        data: usaData,
                    });

                    map.addLayer({
                        id: "skeleton-markets-layer",
                        type: "fill",
                        source: "skeleton-usa",
                        paint: {
                            "fill-color": isDarkTheme ? "#1F2321" : "#F1F4F3",
                            "fill-opacity": 1,
                        },
                    });

                    map.addLayer({
                        id: "skeleton-states-layer",
                        type: "line",
                        source: "skeleton-usa",
                        paint: {
                            "line-color": isDarkTheme ? "#1F2321" : "#F1F4F3",
                            "line-width": 0.5,
                            "line-opacity": 0.3,
                        },
                    });
                } catch (error) {
                    console.error("Failed to load skeleton map:", error);
                }
            };

            loadSkeleton();
        }, [mapIsLoaded, isDarkTheme]);

        // Fetch national average RPM
        useEffect(() => {
            API.get("/analytics/national-average-rpm", {
                params: {
                    trailer: "dry_van",
                    "interval-in-days": "30",
                },
            })
                .then((response) => {
                    setNationalAverageRPM(response.average);
                })
                .catch(() => {
                    handleError();
                });
        }, []);

        // Update colors when data changes
        useEffect(() => {
            const hasData = updateMarketColors();
            setShowSkeleton(!hasData);
        }, [outbounds, optimizePer, nationalAverageRPM, markets.data, mapInitialised, updateMarketColors]);

        // Notify parent when map is initialised
        useEffect(() => {
            if (mapInitialised) {
                onInitialised?.();
            }
        }, [mapInitialised, onInitialised]);

        // Handle selected markets highlighting (only when prop is explicitly provided with markets)
        // Note: PredictionMarketsMap manages this layer itself via its own effect,
        // so we only act here when selectedMarkets is explicitly provided with items
        useEffect(() => {
            if (!mapInitialised) return;
            // Only add layer if selectedMarkets has items - don't remove if empty
            // because the consumer might be managing the layer directly via ref
            if (selectedMarkets.length > 0) {
                addSelectedMarketLayer(selectedMarkets);
            }
        }, [selectedMarkets, mapInitialised, addSelectedMarketLayer]);

        // Handle market clicks
        useEffect(() => {
            if (!mapRef.current || !mapInitialised || !onMarketClick) return;

            const map = mapRef.current;

            const handleClick = (e) => {
                const features = map.queryRenderedFeatures(e.point, { layers: ["markets-layer"] });
                if (features.length > 0) {
                    onMarketClick(features[0].properties);
                }
            };

            map.on("click", "markets-layer", handleClick);

            return () => {
                map.off("click", "markets-layer", handleClick);
            };
        }, [mapRef, mapInitialised, onMarketClick]);

        // Cleanup on unmount
        useEffect(() => {
            return () => {
                pinManager.cleanup();
            };
        }, []);

        const displaySkeleton = externalShowSkeleton !== undefined ? externalShowSkeleton : showSkeleton;

        if (hasError || mapError) {
            return <ErrorComponent className={`h-full lg:min-h-[367px] ${className}`} />;
        }

        return (
            <div className={`relative w-full h-full ${containerClassName}`}>
                <div
                    ref={mapContainer}
                    className={`top-[-40px] sm:top-0 left-0 bottom-0 w-full h-full absolute ${className}`}
                    style={{
                        transition: "opacity 0.4s ease-out",
                    }}
                />
                {displaySkeleton && (
                    <div className="absolute inset-0 pointer-events-none">
                        <div
                            className={`absolute inset-0 animate-shimmer bg-gradient-to-r ${
                                isDarkTheme
                                    ? "from-transparent via-[#131414]/30 to-transparent"
                                    : "from-transparent via-[#FFFFFF]/30 to-transparent"
                            }`}
                            style={{
                                animation: "shimmer 2s infinite",
                            }}
                        />
                    </div>
                )}
                {children}
            </div>
        );
    },
);

MarketMapBase.displayName = "MarketMapBase";
